/**
 * @file ヘッダー左に出す、いま選んでいるまなぶ人の表示と切り替え。
 *
 * @what: 選んでいるまなぶ人の名前をボタンに出す (5文字を超えたら "..." を付けて省略する)。
 *   押すと候補の一覧が開き、選ぶと閉じる。開くと選択中の候補に焦点が移り、↑↓ で候補を
 *   移動、Esc と外側のクリックで閉じる (Esc はボタンへ焦点を戻す)。
 * @why: まなぶ人の切り替えが本文のタブに埋もれていた (代表 2026-08-29)。常に見える
 *   ヘッダーへ出す。ボタンに出す名前は省略するので、だれを選んでいるかは aria-label に
 *   省略しない名前で持たせる。※なぜなら読み上げと触覚での操作では、省略した名前だけでは
 *   だれの記録かを確かめられないためである。
 * @failure: 描画のみ。候補が空のとき、まなぶ人がまだ決まっていないときは何も出さない。
 */

import { useEffect, useRef, useState } from "react";
import { type HeaderLearner, truncateLearnerLabel } from "./headerLearnerContext";

export function LearnerSwitcher({ learner }: { learner: HeaderLearner | null }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const items = () =>
    Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? []);

  // 家族を切り替えるとまなぶ人が一度 null に戻る。開いたままにすると、次の家族の候補で
  // 一覧が勝手に開き直すので閉じる。
  useEffect(() => {
    if (learner === null) setOpen(false);
  }, [learner]);

  useEffect(() => {
    if (!open) return;
    const list = items();
    // 選択中の候補に焦点を置く (menuitemradio の作法)。
    (list.find((el) => el.getAttribute("aria-checked") === "true") ?? list[0])?.focus();
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const current = items();
      if (current.length === 0) return;
      e.preventDefault();
      const at = current.findIndex((el) => el === document.activeElement);
      const next =
        e.key === "ArrowDown"
          ? (at + 1) % current.length
          : (at - 1 + current.length) % current.length;
      current[next]?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (learner === null || learner.options.length === 0) return null;
  // まなぶ人が決まる前の一瞬は何も出さない。既定で先頭を選んだように見せると、本文が
  // まだだれの記録も開いていないのに選択済みだと伝えてしまう。
  const selected = learner.options.find((o) => o.id === learner.selectedId) ?? null;
  if (selected === null) return null;

  return (
    <div className="app-learner" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="app-learner-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`まなぶ人を変える (いまは${selected.label})`}
        onClick={() => setOpen((o) => !o)}
      >
        {truncateLearnerLabel(selected.label)}
        <span aria-hidden="true" className="app-learner-caret">
          ▾
        </span>
      </button>
      {open ? (
        <div className="app-learner-list" role="menu" aria-label="まなぶ人を選ぶ">
          {learner.options.map((o) => (
            <button
              key={o.id}
              role="menuitemradio"
              aria-checked={o.id === selected.id}
              type="button"
              className="app-learner-item"
              onClick={() => {
                learner.onSelect(o.id);
                setOpen(false);
                buttonRef.current?.focus();
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
